import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clock, CreditCard, Globe, Lock } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { CardFields, emptyCard, type CardForm } from "@/components/billing/CardFields";
import { MethodIcon, PayIcon } from "@/components/billing/PayIcon";
import {
  PAY_METHOD_LABELS,
  PaymentMethodSelector,
  type PayMethod,
} from "@/components/billing/PaymentMethodSelector";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import { useCardEntry } from "@/components/billing/useCardEntry";
import { useDirectDebit, type DirectDebitEntry } from "@/components/billing/useDirectDebit";
import { useWalletSetup, type ApplePayRecurring } from "@/components/billing/useWalletSetup";
import {
  Button,
  ErrorAlert,
  ErrorState,
  Form,
  FullPageSpinner,
  cx,
  Section,
  useToast,
} from "@/components/ui";
import { portal } from "@/lib/api";
import { methodDetail, methodProblem, processorName, type CardErrors } from "@/lib/payments";
import { supportsApplePay, supportsGooglePay, type Wallet } from "@/lib/stripe";
import type { PaymentMethod } from "@/lib/types";
import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { BackLink, PageTitle, SectionTitle } from "@/components/billing/parts";

/** What each wallet's tab says. */
const WALLET_COPY: Record<
  Wallet,
  {
    lead: string;
    detail: string;
    unavailable: string;
    elsewhere: string;
    defaultTitle: string;
    defaultDetail: string;
  }
> = {
  apple_pay: {
    lead: "Approve with Face ID or Touch ID. Nothing is charged today.",
    detail:
      "Apple Pay is kept for your plan and domain renewals. Payments you make here, such as an upgrade or a new domain, use a card.",
    unavailable:
      "Apple Pay isn't ready on this device. Add a card to Apple Wallet and try again, or add a card instead.",
    elsewhere:
      "Apple Pay works in Safari on an iPhone, iPad or Mac with a card in Apple Wallet. Open this page there to add it, or add a card instead.",
    defaultTitle: "Make Apple Pay the default for renewals",
    defaultDetail: "Your subscription and domain renewals will be charged to it",
  },
  google_pay: {
    lead: "Choose a card saved in your Google account. Nothing is charged today.",
    detail: "Google Pay works like any card on file: for domains, your plan and its renewals.",
    unavailable: "Google Pay isn't available in this browser. Try Chrome or Edge, or add a card instead.",
    elsewhere:
      "Google Pay needs a secure (https) page. Open this page over https to add it, or add a card instead.",
    defaultTitle: "Make this the default card for all charges",
    defaultDetail: "Your subscription, domain renewals and mailbox charges will use it",
  },
};

/** Figma "Billing — Add payment method · Free (no charge)". The Apple Pay and Google Pay tabs have no
 * Figma frame; they follow the card tab, with the wallet's own button in place of "Add card". */
export function AddPaymentMethodPage() {
  const { orgId, can, permissionsLoading } = useOrganization();
  // Google Pay has its own tab here, so it stays out of the card form.
  const entry = useCardEntry(orgId, { googlePay: false });
  const user = useUser();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const methods = useQuery({
    queryKey: ["payment-methods", orgId],
    queryFn: () => portal.paymentMethods(orgId),
  });
  const sub = useQuery({ queryKey: ["subscription", orgId], queryFn: () => portal.subscription(orgId) });
  const [card, setCard] = useState<CardForm>(emptyCard(user.display_name ?? ""));
  const [errors, setErrors] = useState<CardErrors>({});
  const [makeDefault, setMakeDefault] = useState(true);
  const [params] = useSearchParams();
  // Back from Stripe's Direct Debit page: open on that tab.
  const [method, setMethod] = useState<PayMethod>(
    params.get("method") === "bacs_debit" ? "bacs_debit" : "card",
  );
  const directDebit = useDirectDebit(orgId);
  const saved = async (m: PaymentMethod) => {
    await qc.invalidateQueries({ queryKey: ["payment-methods", orgId] });
    navigate(`/billing/payment-methods/${m.id}/saved`, { replace: true });
  };
  const done = directDebit.returned.state === "done" ? directDebit.returned.method : null;
  useEffect(() => {
    if (done) void saved(done);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the bank account is kept
  }, [done]);
  const add = useMutation({ mutationFn: () => entry.addCard(card, { makeDefault }), onSuccess: saved });
  const toast = useToast();
  const setDefault = useMutation({
    mutationFn: (id: string) => portal.makeDefaultPaymentMethod(orgId, id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["payment-methods", orgId] });
      toast.show({ tone: "success", title: "Default payment method changed" });
    },
  });
  // Apple Pay shows the renewals it will pay for (and then issues a token that survives a new phone).
  const recurring = useMemo<ApplePayRecurring | null>(() => {
    const next = sub.data?.upcoming_charge;
    if (!sub.data || !next) return null;
    const { plan, billing_period } = sub.data.account;
    return {
      label: `${plan.name} plan`,
      amountCents: next.total_cents,
      interval: billing_period === "annual" ? "year" : "month",
      startsAt: next.date,
    };
  }, [sub.data]);

  if (permissionsLoading) return <FullPageSpinner />;
  if (!can("billing:manage")) return <Navigate to="/billing" replace />;
  if (methods.isPending) return <FullPageSpinner />;
  if (methods.isError) return <ErrorState error={methods.error} onRetry={() => void methods.refetch()} />;
  const onFile = methods.data.payment_methods;
  const first = onFile.length === 0;
  const processor = processorName(methods.data.provider);
  // A wallet's own button only where it can show (Apple Pay: Safari on an Apple device; Google Pay:
  // an HTTPS page), and only through Stripe; elsewhere its tab says so.
  const wallet: Wallet | null = method === "apple_pay" || method === "google_pay" ? method : null;
  const walletHere =
    methods.data.provider === "stripe" &&
    (wallet === "apple_pay" ? supportsApplePay() : wallet === "google_pay" && supportsGooglePay());
  const cancel = () => navigate("/billing?tab=payment-methods");

  const submit = async () => {
    const found = await entry.validate(card);
    setErrors(found);
    if (!Object.keys(found).length) {
      add.mutate();
      return;
    }
    // The name field is a native required control: the shared Form already caught and focused it
    // before this ran. Any other card problem is Stripe's own form, so its element moves focus itself.
    if (card.name.trim() && "card" in found) entry.focus();
  };

  return (
    <div className="flex flex-col gap-6">
      <BackLink to="/billing?tab=payment-methods" />
      <PageTitle
        title="Add a payment method"
        subtitle="Nothing is charged. A card is used only when you buy a domain or move to a paid plan."
      />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <Section variant="compact">
            <SectionTitle>Cards on file</SectionTitle>
            <div className="h-3" />
            {first ? (
              <p className="flex items-center gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted">
                <CreditCard className="size-[18px] shrink-0" aria-hidden />
                No cards yet. This is the first one, so it becomes the default for anything you buy.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                <ErrorAlert error={setDefault.error} />
                {onFile.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-[10px] bg-[#faf6ef] px-3.5 py-3">
                    <MethodIcon method={m} className="text-fg-muted" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-fg">{m.label}</p>
                      <p className={cx("text-xs", methodProblem(m) ? "text-[#b8291f]" : "text-[#766e6a]")}>
                        {methodDetail(m, { renewals: true })}
                      </p>
                    </div>
                    {m.is_default ? (
                      <span className="ml-auto text-xs font-semibold text-[#766e6a]">Default</span>
                    ) : (
                      <button
                        type="button"
                        disabled={setDefault.isPending}
                        aria-busy={setDefault.isPending}
                        onClick={() => setDefault.mutate(m.id)}
                        className="ml-auto text-xs font-semibold text-brand-500 hover:underline disabled:opacity-60"
                      >
                        Make default<span className="sr-only">: {m.label}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section variant="compact">
            <SectionTitle>{method === "card" ? "Card details" : PAY_METHOD_LABELS[method]}</SectionTitle>
            <PaymentMethodSelector value={method} onChange={setMethod} />
            <div className="h-[30px]" />
            {method === "bacs_debit" ? (
              <BankDebitPanel
                entry={directDebit}
                connected={methods.data.provider !== "none"}
                country={sub.data?.account.country ?? null}
                defaultChoice={
                  <DefaultChoice
                    checked={first || makeDefault}
                    locked={first}
                    onChange={setMakeDefault}
                    title="Make Direct Debit the default for renewals"
                    detail="Your subscription and domain renewals will be debited from this account"
                  />
                }
                onStart={() =>
                  void directDebit.start({
                    makeDefault: first || makeDefault,
                    params: { method: "bacs_debit" },
                  })
                }
                onCancel={cancel}
              />
            ) : wallet && !walletHere ? (
              <WalletElsewhere
                wallet={wallet}
                connected={methods.data.provider === "stripe"}
                onCancel={cancel}
              />
            ) : wallet ? (
              <WalletPanel
                key={wallet}
                orgId={orgId}
                wallet={wallet}
                // Renewals charge the default card: Apple's sheet lists them only if this will be it.
                recurring={first || makeDefault ? recurring : null}
                makeDefault={first || makeDefault}
                onSaved={(m) => void saved(m)}
                defaultChoice={
                  <DefaultChoice
                    checked={first || makeDefault}
                    locked={first}
                    onChange={setMakeDefault}
                    title={WALLET_COPY[wallet].defaultTitle}
                    detail={WALLET_COPY[wallet].defaultDetail}
                  />
                }
                onCancel={cancel}
              />
            ) : (
              <Form
                error={add.error}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (method === "card") void submit();
                }}
              >
                <ErrorAlert error={add.error} />
                {method === "card" ? (
                  <CardFields entry={entry} value={card} onChange={setCard} errors={errors} />
                ) : (
                  <p
                    role="status"
                    className="flex items-start gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted"
                  >
                    <Clock className="mt-0.5 size-[18px] shrink-0" aria-hidden />
                    {PAY_METHOD_LABELS[method]} isn't switched on yet. It appears here once it's connected.
                  </p>
                )}
                <div className="h-4" />
                <DefaultChoice
                  checked={first || makeDefault}
                  locked={first}
                  onChange={setMakeDefault}
                  title="Make this the default card for all charges"
                  detail="Your subscription, domain renewals and mailbox charges will use this card"
                />
                <div className="h-[18px]" />
                <div className="flex gap-2.5">
                  <Button
                    unstyled
                    type="submit"
                    loading={add.isPending}
                    disabledReason={
                      method !== "card" ? `${PAY_METHOD_LABELS[method]} isn't switched on yet.` : undefined
                    }
                    className="rounded-[10px] bg-brand-500 px-[18px] py-3 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                  >
                    {add.isPending ? "Checking with your bank…" : "Add card"}
                  </Button>
                  <CancelButton onClick={cancel} />
                </div>
              </Form>
            )}
            {methods.data.provider === "stripe" ? (
              <PoweredByStripe provider={methods.data.provider} className="mt-3.5" />
            ) : (
              <p className="mt-3.5 flex items-center gap-2 text-xs text-fg-subtle">
                <Lock className="size-3.5" aria-hidden /> Payments processed by{" "}
                <span className="text-sm font-bold text-fg-muted">{processor}</span>
              </p>
            )}
          </Section>
        </div>

        <div className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
          <Section variant="compact">
            <SectionTitle size="md">What happens when you add it</SectionTitle>
            <div className="h-2.5" />
            <Note icon={<Clock className="size-[15px]" aria-hidden />}>
              Nothing is charged to this card today
            </Note>
            <Note icon={<Lock className="size-[15px]" aria-hidden />}>
              {processor} runs a small authorisation check with your bank, then reverses it
            </Note>
            <Note icon={<Globe className="size-[15px]" aria-hidden />}>
              It is used for domain purchases, and for a paid plan if you upgrade later
            </Note>
          </Section>
          <Section variant="compact">
            <p className="flex items-center gap-2 text-base leading-6 font-semibold text-fg">
              <Lock className="size-4" aria-hidden /> Card security
            </p>
            <p className="mt-2 text-xs text-fg-muted">
              Card details go straight to {processor}, our payment processor. CortexGrip stores only the card
              type, last four digits and expiry date, never the full number.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

/**
 * The Bank debit tab: a UK bank account set up on Stripe's own page (the only place Stripe collects a
 * Direct Debit mandate), then back here. No Figma frame: it follows the wallet tabs, with "Set up Direct
 * Debit" where they have the wallet's button.
 */
function BankDebitPanel({
  entry,
  connected,
  country,
  defaultChoice,
  onStart,
  onCancel,
}: {
  entry: DirectDebitEntry;
  connected: boolean;
  /** The billing address's country: Direct Debit is for UK bank accounts. */
  country: string | null;
  defaultChoice: ReactNode;
  onStart: () => void;
  onCancel: () => void;
}) {
  const { returned } = entry;
  const elsewhere = country !== null && country !== "GB";
  const busy = entry.starting || returned.state === "completing" || returned.state === "done";
  return (
    <div>
      <ErrorAlert error={entry.error ?? (returned.state === "error" ? returned.error : null)} />
      <div
        role="status"
        className="flex items-start gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted"
      >
        <PayIcon method="bacs_debit" className="mt-0.5 size-[18px]" />
        {!connected ? (
          <span>Bank debit isn't switched on yet. It appears here once payments are connected.</span>
        ) : elsewhere ? (
          <span>
            Direct Debit is for UK bank accounts, and your billing address is outside the UK. Add a card
            instead.
          </span>
        ) : returned.state === "completing" || returned.state === "done" ? (
          <span>Saving your Direct Debit…</span>
        ) : (
          <span className="min-w-0">
            <span className="block">
              {returned.state === "cancelled"
                ? "You left before finishing, so nothing was set up. Start again when you're ready."
                : "Set up a Direct Debit from a UK bank account. You enter your bank details on Stripe's secure page, then come straight back. Nothing is charged today."}
            </span>
            <span className="mt-1 block text-xs text-[#766e6a]">
              Direct Debit pays your plan and domain renewals, and can pay a plan upgrade (it starts once the
              payment clears). Buying a domain or paying an invoice now uses a card.
              <HelpHint helpKey={HELP_KEYS.billing.directDebit} label="Direct Debit" />
            </span>
          </span>
        )}
      </div>
      {connected && !elsewhere && (
        <>
          <div className="h-4" />
          {defaultChoice}
        </>
      )}
      <div className="h-[18px]" />
      <div className="flex gap-2.5">
        {connected && !elsewhere && (
          <button
            type="button"
            onClick={onStart}
            disabled={busy}
            aria-busy={busy}
            className="rounded-[10px] bg-brand-500 px-[18px] py-3 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {entry.starting ? "Opening Stripe…" : "Set up Direct Debit"}
          </button>
        )}
        <CancelButton onClick={onCancel} />
      </div>
    </div>
  );
}

/** A wallet's tab where its button can't show (another browser or device, no HTTPS) or without Stripe. */
function WalletElsewhere({
  wallet,
  connected,
  onCancel,
}: {
  wallet: Wallet;
  connected: boolean;
  onCancel: () => void;
}) {
  return (
    <div>
      <p
        role="status"
        className="flex items-start gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted"
      >
        <PayIcon method={wallet} className="mt-0.5 size-[18px]" />
        {connected
          ? WALLET_COPY[wallet].elsewhere
          : `${PAY_METHOD_LABELS[wallet]} isn't switched on yet. It appears here once Stripe is connected.`}
      </p>
      <div className="h-[18px]" />
      <CancelButton onClick={onCancel} />
    </div>
  );
}

/**
 * The Apple Pay or Google Pay tab: the wallet's own button where "Add card" sits on the card tab.
 * Pressing it opens the wallet's payment sheet; nothing is charged, the card is kept.
 */
function WalletPanel({
  orgId,
  wallet,
  recurring,
  makeDefault,
  onSaved,
  defaultChoice,
  onCancel,
}: {
  orgId: string;
  wallet: Wallet;
  recurring: ApplePayRecurring | null;
  makeDefault: boolean;
  onSaved: (method: PaymentMethod) => void;
  defaultChoice: ReactNode;
  onCancel: () => void;
}) {
  const { status, slot, saving, error, retry } = useWalletSetup(orgId, wallet, {
    recurring,
    makeDefault,
    onSaved,
  });
  const unavailable = status === "unavailable";
  const copy = WALLET_COPY[wallet];
  return (
    <div>
      <ErrorAlert error={error} />
      {retry && (
        <button
          type="button"
          onClick={retry}
          disabled={saving}
          aria-busy={saving}
          className="mb-3.5 text-sm font-semibold text-brand-500 hover:underline disabled:opacity-60"
        >
          Try saving again
        </button>
      )}
      <div
        role="status"
        className="flex items-start gap-3 rounded-[10px] bg-[#faf6ef] p-4 text-sm text-fg-muted"
      >
        <PayIcon method={wallet} className="mt-0.5 size-[18px]" />
        {unavailable ? (
          <span>{copy.unavailable}</span>
        ) : (
          <span className="min-w-0">
            <span className="block">{copy.lead}</span>
            <span className="mt-1 block text-xs text-[#766e6a]">{copy.detail}</span>
          </span>
        )}
      </div>
      <div className="h-4" />
      {defaultChoice}
      <div className="h-[18px]" />
      <div className="flex items-center gap-2.5">
        {/* Always mounted (hidden when the wallet is unavailable): unmounting would reload the button. */}
        <div className={cx("relative h-11 w-[200px] shrink-0", unavailable && "hidden")}>
          {status === "loading" && (
            <div className="absolute inset-0 animate-pulse rounded-[10px] bg-muted" aria-hidden />
          )}
          <div ref={slot} className={cx(saving && "pointer-events-none opacity-60")} />
        </div>
        <CancelButton onClick={onCancel} />
      </div>
      {saving && (
        <p role="status" className="mt-2.5 text-xs text-fg-muted">
          Saving {PAY_METHOD_LABELS[wallet]}…
        </p>
      )}
    </div>
  );
}

function DefaultChoice({
  checked,
  locked,
  onChange,
  title,
  detail,
}: {
  checked: boolean;
  /** The first card is always the default. */
  locked: boolean;
  onChange: (on: boolean) => void;
  title: string;
  detail: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-[10px] bg-[#faf6ef] px-3.5 py-3">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className="mt-px flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border border-[#d4cac4] bg-surface text-white peer-checked:border-brand-500 peer-checked:bg-brand-500 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/40"
      >
        <Check className="size-3" strokeWidth={3} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-fg">{title}</span>
        <span className="block text-xs text-[#766e6a]">{detail}</span>
      </span>
    </label>
  );
}

function CancelButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[10px] border border-border bg-surface px-[18px] py-3 text-sm font-semibold text-[#443e3b] hover:bg-muted"
    >
      Cancel
    </button>
  );
}

function Note({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <p className="flex items-start gap-2.5 py-[9px] text-xs text-fg-muted">
      <span className="mt-px shrink-0">{icon}</span>
      {children}
    </p>
  );
}
